import {Either, getEq, isLeft, Left, left, Right, right} from 'fp-ts/lib/Either';
import {Eq, eqString, getStructEq} from 'fp-ts/lib/Eq';
import {ToMatchOptions} from '../../Shared/utilities/MapDiscriminatedUnion';
import {Async, getAsyncEq, isLoaded, Loaded} from './Async';

export class EmptyPhoneNumber {
  public readonly key = 'UnvalidatedPhoneNumber';
  public readonly phoneNumber = '';
}

export class InvalidPhoneNumber {
  public static create(phoneNumber: string) {
    return phoneNumber === '' ? new EmptyPhoneNumber() : new InvalidPhoneNumber(phoneNumber);
  }
  public readonly key = 'InvalidPhoneNumber';
  public readonly validationError = 'Please enter a valid phone number.';

  private constructor(public readonly phoneNumber: string) {}
}

export class ValidPhoneNumber {
  public static create(phoneNumber: string) {
    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.replace(/\D/g, '').slice(1);
    }
    const isValid = /^\d{10}$/.test(phoneNumber);

    return isValid ? new ValidPhoneNumber(phoneNumber) : InvalidPhoneNumber.create(phoneNumber);
  }

  public readonly key = 'ValidPhoneNumber';
  private constructor(public readonly phoneNumber: string) {}
}

export type PhoneNumber = ValidPhoneNumber | InvalidPhoneNumber | EmptyPhoneNumber;

export type PhoneOptions<TReturn> = ToMatchOptions<PhoneNumber, 'key', TReturn>;

export const matchPhoneNumber = <TReturn>(phoneNumber: PhoneNumber, options: PhoneOptions<TReturn>): TReturn => {
  switch (phoneNumber.key) {
    case 'InvalidPhoneNumber':
      return options.InvalidPhoneNumber(phoneNumber);
    case 'UnvalidatedPhoneNumber':
      return options.UnvalidatedPhoneNumber(phoneNumber);
    case 'ValidPhoneNumber':
      return options.ValidPhoneNumber(phoneNumber);
  }
};

export const eqPhoneNumber = getStructEq({
  phoneNumber: eqString,
});

export type ValidatablePhone = Async<Either<PhoneNumber, ValidPhoneNumber>>;

export type ValidatedPhoneNumber = Loaded<Right<ValidPhoneNumber>>;

export type OptionalPhoneNumber = ValidatedPhoneNumber | Loaded<Left<EmptyPhoneNumber>>;

export const isEmptyPhoneNumber = (phone: ValidatablePhone): phone is Loaded<Left<EmptyPhoneNumber>> =>
  isLoaded(phone) && isLeft(phone.Data) && phone.Data.left instanceof EmptyPhoneNumber;

export const eqValidatablePhone: Eq<ValidatablePhone> = getAsyncEq(getEq(eqPhoneNumber, eqPhoneNumber));

export const createValidatedPhone = (value: string): Loaded<Right<ValidPhoneNumber>> =>
  new Loaded(right(ValidPhoneNumber.create(value) as ValidPhoneNumber)) as Loaded<Right<ValidPhoneNumber>>;

export const createValidatableEmptyPhone = (): Loaded<Left<EmptyPhoneNumber>> =>
  new Loaded(left(new EmptyPhoneNumber())) as Loaded<Left<EmptyPhoneNumber>>;

export const isValidPhoneNumber = (phone: PhoneNumber): phone is ValidPhoneNumber => phone.key === 'ValidPhoneNumber';

export const isUnvalidatedPhoneNumber = (phone : PhoneNumber) : phone is EmptyPhoneNumber => phone.key === 'UnvalidatedPhoneNumber';
